import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@drpcrd/utils/shared";
import { forwardRef, HTMLAttributes, ReactNode } from "react";

const statusAlertVariants = cva(
  "flex w-full flex-col items-start gap-2 rounded-2xl px-3 py-2 md:h-[70px] md:flex-row md:items-center md:justify-between",
  {
    variants: {
      variant: {
        default: "",
        outlined: "ring-1 ring-inset ring-neutral-500",
      },
      color: {
        default: "bg-primary-50",
        white: "bg-white",
        neutral: "bg-neutral-100",
      },
    },
    defaultVariants: {
      variant: "default",
      color: "default",
    },
  },
);

type StatusAlertProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof statusAlertVariants>;

/**
 * A flexible, composable banner component for displaying informational messages with optional actions.
 * Use with StatusAlertIcon, StatusAlertContent, and StatusAlertActions for full customization.
 */
export const StatusAlert = forwardRef<HTMLDivElement, StatusAlertProps>(
  ({ className, variant, color, ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(statusAlertVariants({ variant, color }), className)}
      {...props}
    />
  ),
);
StatusAlert.displayName = "StatusAlert";

type StatusAlertIconProps = HTMLAttributes<HTMLSpanElement> & {
  children: ReactNode;
};

/**
 * Icon container for StatusAlert. Accepts emoji or icon elements.
 */
export const StatusAlertIcon = forwardRef<
  HTMLSpanElement,
  StatusAlertIconProps
>(({ className, ...props }, ref) => (
  <span ref={ref} className={cn("shrink-0 text-2xl", className)} {...props} />
));
StatusAlertIcon.displayName = "StatusAlertIcon";

type StatusAlertContentProps = HTMLAttributes<HTMLDivElement>;

/**
 * Content container for StatusAlert. Wraps the main text/description area.
 */
export const StatusAlertContent = forwardRef<
  HTMLDivElement,
  StatusAlertContentProps
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-1 items-center justify-start gap-2", className)}
    {...props}
  />
));
StatusAlertContent.displayName = "StatusAlertContent";

type StatusAlertActionsProps = HTMLAttributes<HTMLDivElement>;

/**
 * Actions container for StatusAlert. Place Button components here for alert actions.
 */
export const StatusAlertActions = forwardRef<
  HTMLDivElement,
  StatusAlertActionsProps
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-1", className)}
    {...props}
  />
));
StatusAlertActions.displayName = "StatusAlertActions";
